// src/registered/site-admin/report_component_library/seedComponents.js
//
// Starter example components — the Component Library equivalent of the
// hard-coded example cards on "Enter ESG Data"
// (report_management/blueprint/blueprintReportingTemplates.js).
//
// They are authored the way the toolset would: one component per topic, each
// binding the ESRS disclosure datapoints that topic reports, with a per-
// disclosure label / render type / guidance. Because a seed cannot know which
// taxonomy version is synced, every disclosure is a MANUAL entry (rowId null) —
// open one in the editor and re-bind each block to the live taxonomy if you
// want a hard reference.
//
// A seed shows in the library list and opens read-through; editing + Save
// persists it as a real component (local + cloud) and the saved copy wins
// from then on.

import ReportComponentDTO from "./ReportComponentDTO";

/** compact manual-disclosure block */
const d = (measureName, renderType, guidance) => ({
  rowId: null,
  xbrlTag: "",
  headerId: null,
  measureName,
  label: measureName,
  renderType,
  guidance,
});

export const SEED_PREFIX = "seed_";

const RAW_SEEDS = [
  {
    id: `${SEED_PREFIX}transport`,
    label: "Transport & Logistics Emissions",
    categoryTag: "Example — Arethia blueprint",
    boundDisclosures: [
      d(
        "Gross Scope 1 GHG emissions",
        "NUMERIC_VALUE",
        "Direct emissions including fuel burned in owned or leased vehicles (mobile combustion). Report in tCO2e.",
      ),
      d(
        "Gross Scope 3 GHG emissions — Upstream transportation and distribution",
        "NUMERIC_VALUE",
        "Inbound freight arranged by the undertaking. tCO2e, e.g. GLEC or DEFRA factors applied to tonne-km.",
      ),
      d(
        "Gross Scope 3 GHG emissions — Downstream transportation and distribution",
        "NUMERIC_VALUE",
        "Outbound freight to customers. tCO2e.",
      ),
      d(
        "Gross Scope 3 GHG emissions — Business travel",
        "NUMERIC_VALUE",
        "Air, rail and road business travel. tCO2e.",
      ),
      d(
        "Gross Scope 3 GHG emissions — Employee commuting",
        "NUMERIC_VALUE",
        "From an employee survey, or headcount multiplied by an average factor. tCO2e.",
      ),
      d(
        "Description of the methodologies, assumptions and emission factors used",
        "TEXT",
        "Activity-data sources, the factor set (GLEC / DEFRA / BEIS), what is included and excluded, and any location- versus market-based treatment.",
      ),
    ],
  },
  {
    id: `${SEED_PREFIX}energy`,
    label: "Energy — Electricity, Heating & Lighting",
    categoryTag: "Example — Arethia blueprint",
    boundDisclosures: [
      d(
        "Total energy consumption",
        "NUMERIC_VALUE",
        "All energy consumed in own operations for the period. Report in MWh.",
      ),
      d(
        "Total energy consumption from fossil sources",
        "NUMERIC_VALUE",
        "MWh. Natural gas, heating oil, LPG, non-renewable grid electricity, purchased heat and cooling.",
      ),
      d(
        "Total energy consumption from renewable sources",
        "NUMERIC_VALUE",
        "MWh. Renewable electricity (green tariff, PPA, guarantees of origin), self-generated PV consumed on site, renewable heat.",
      ),
      d(
        "Percentage of renewable sources in total energy consumption",
        "PERCENTAGE",
        "Renewable energy divided by total energy consumption.",
      ),
      d(
        "Gross Scope 2 GHG emissions (location-based)",
        "NUMERIC_VALUE",
        "Purchased electricity, heat, steam and cooling at grid-average factors. tCO2e.",
      ),
      d(
        "Gross Scope 2 GHG emissions (market-based)",
        "NUMERIC_VALUE",
        "Purchased energy reflecting contractual instruments (green tariff, PPA, guarantees of origin). tCO2e.",
      ),
      d(
        "Description of energy-efficiency and renewable-energy measures",
        "TEXT",
        "Heat pumps, heat recovery, photovoltaics, LED and lighting controls, building fabric; and the results achieved.",
      ),
    ],
  },
  {
    id: `${SEED_PREFIX}water`,
    label: "Water Withdrawal & Discharge",
    categoryTag: "Example — Arethia blueprint",
    boundDisclosures: [
      d(
        "Total water withdrawal",
        "NUMERIC_VALUE",
        "All water drawn into the site or sites for the period. Report in m³.",
      ),
      d(
        "Total water consumption",
        "NUMERIC_VALUE",
        "Withdrawal minus discharge — water not returned (evaporation, product content, irrigation). m³.",
      ),
      d(
        "Total water consumption in areas at water risk, including areas of high-water stress",
        "NUMERIC_VALUE",
        "The consumption figure for sites in water-stressed basins (WRI Aqueduct, WWF Water Risk Filter, or the local regulator).",
      ),
      d(
        "Total water recycled and reused",
        "NUMERIC_VALUE",
        "Volume of process or cleaning water recirculated rather than withdrawn fresh. m³.",
      ),
      d(
        "Total water discharge",
        "NUMERIC_VALUE",
        "Wastewater leaving the site or sites. m³. Record the treatment route and destination in the narrative.",
      ),
      d(
        "Whether the site is located in an area of high water stress",
        "BOOLEAN",
        "Yes or No, with the basis of assessment recorded in the narrative.",
      ),
      d(
        "Description of water management, targets and any incidents",
        "TEXT",
        "Policies and reduction targets, key discharge-quality measurements (COD, BOD, pH, nitrogen), and any permit exceedances or spills in the period.",
      ),
    ],
  },
];

export const SEED_COMPONENTS = RAW_SEEDS.map((s) => new ReportComponentDTO(s));

export const isSeedId = (id) => String(id || "").startsWith(SEED_PREFIX);
